import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import { useTranslation } from 'react-i18next';
import * as styles from './contactUs.module.scss';
import PageLayout from '../components/PageLayout';
import WeContactYou from '../components/WeContactYou';
import GenericLink from '../components/GenericLink';
import SEO from '../components/SEO';
import CareersSection from '../components/CareersSection';

const ContactUsPage = (): React.ReactElement => {
  const { t } = useTranslation();
  const data: any = useStaticQuery(
    graphql`
      query GetContactUsData {
        site {
          siteMetadata {
            googleMapsAPIKey
          }
        }
        sanityContactUsPage {
          showMap
          address
          googleMapsAddress
          phoneNumbers
          emails
          careersSection {
            title
            text
            image {
              asset {
                fluid(maxWidth: 1920) {
                  ...GatsbySanityImageFluid
                }
              }
            }
          }
        }
      }
    `,
  );
  const { googleMapsAPIKey } = data.site.siteMetadata;
  const { showMap, address, googleMapsAddress, phoneNumbers, emails, careersSection } =
    data.sanityContactUsPage || {};

  return (
    <PageLayout
      title={t('contact_us_page.title', 'Contact us')}
      extraSectionChildren={
        <>
          <WeContactYou></WeContactYou>
          {careersSection &&
            careersSection.title &&
            careersSection.text &&
            careersSection?.image?.asset?.fluid && <CareersSection />}
        </>
      }
      withPageContentFullWidth
      withoutCircles={!showMap}
    >
      <SEO title={t('contact_us_page.title', 'Contact us')} urlPath="/contactUs" />
      {!!address && showMap && (
        <div>
          <h2 className={styles.subTitle}>{t('contact_us_page.map_label', 'How to find us')}</h2>
          <iframe
            height="400"
            frameBorder="0"
            style={{ width: '100%', border: 0 }}
            src={`https://www.google.com/maps/embed/v1/place?q=${
              googleMapsAddress || address
            }&key=${googleMapsAPIKey}`}
            allowFullScreen
          ></iframe>
        </div>
      )}
      {(!!address ||
        (!!phoneNumbers && phoneNumbers.length > 0) ||
        (!!emails && emails.length > 0)) && (
        <div className={styles.content}>
          {!!address && (
            <div className={styles.infoBlock}>
              <h2 className={styles.subTitle}>
                {t('contact_us_page.address_label', 'Where we are')}
              </h2>
              <span className={styles.info}>
                <GenericLink
                  noExternalUrlIcon
                  to={
                    'https://www.google.com/maps/search/?api=1&query=' +
                    encodeURI(googleMapsAddress || address)
                  }
                >
                  {address}
                </GenericLink>
              </span>
            </div>
          )}
          {!!phoneNumbers && phoneNumbers.length > 0 && (
            <div className={styles.infoBlock}>
              <h2 className={styles.subTitle}>{t('contact_us_page.phones_label', 'Talk to us')}</h2>
              {phoneNumbers.map((phoneNumber: string, i: number) => (
                <span key={i} className={styles.info}>
                  <GenericLink noExternalUrlIcon to={'tel:' + phoneNumber}>
                    {phoneNumber}
                  </GenericLink>
                </span>
              ))}
            </div>
          )}
          {!!emails && emails.length > 0 && (
            <div className={styles.infoBlock}>
              <h2 className={styles.subTitle}>
                {t('contact_us_page.emails_label', 'Write to us')}
              </h2>
              {emails.map((email: string, i: number) => (
                <span key={i} className={styles.info}>
                  <GenericLink noExternalUrlIcon to={'mailto:' + email}>
                    {email}
                  </GenericLink>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default ContactUsPage;
